import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  TextField,
  Typography,
  Stack,
  Link,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithCustomToken,
  ConfirmationResult,
} from 'firebase/auth';
import { initializeFirebase } from '../config/firebase';
import { MuiTelInput } from 'mui-tel-input';
import { Phone, Email } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth';
import { loginSchema, LoginFormData } from '../validations/auth';

export default function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin, isAuthenticated, isProfileLoaded } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  // const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [waitingForAuth, setWaitingForAuth] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  type LoginMode = 'email' | 'phone';
  const [loginMode, setLoginMode] = useState<LoginMode>('email');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [sendingSms, setSendingSms] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const confirmationResultRef = useRef<ConfirmationResult | null>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const from = location.state?.from?.pathname || '/';

  // Watch for authentication state changes and navigate when ready
  useEffect(() => {
    if (waitingForAuth && isAuthenticated && isProfileLoaded) {
      console.log('Auth state ready, navigating to:', from);
      setWaitingForAuth(false);
      navigate(from, { replace: true });
    }
  }, [waitingForAuth, isAuthenticated, isProfileLoaded, from, navigate]);

  // Add timeout for authentication waiting
  useEffect(() => {
    if (waitingForAuth) {
      const timeout = setTimeout(() => {
        setWaitingForAuth(false);
        navigate(from, { replace: true });
      }, 5000); // 5 second timeout

      return () => clearTimeout(timeout);
    }
  }, [waitingForAuth, from, navigate]);

  // Cleanup reCAPTCHA and countdown on unmount
  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      saveId: false,
      keepSignedIn: false,
    },
  });

  const resetPhoneState = () => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setPhoneNumber('');
    setSmsSent(false);
    setSmsCode('');
    setCountdown(0);
    setSendingSms(false);
    setIsVerifying(false);
    setPhoneError(null);
    confirmationResultRef.current = null;
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);

      // Use Firebase authentication directly
      await authLogin({
        email: data.email,
        password: data.password,
      });

      console.log('Login successful, setting waiting state...'); // Debug log

      // Set waiting state - the useEffect will handle navigation when auth is ready
      setWaitingForAuth(true);

    } catch (error: unknown) {
      console.error('Login error:', error);

      // Handle Firebase authentication errors
      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string; message?: string };
        switch (firebaseError.code) {
          case 'auth/user-not-found':
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            setError('root', {
              type: 'manual',
              message: t('auth.errors.invalidCredentials'),
            });
            break;
          case 'auth/invalid-email':
            setError('root', {
              type: 'manual',
              message: t('auth.errors.invalidEmail'),
            });
            break;
          case 'auth/too-many-requests':
            setError('root', {
              type: 'manual',
              message: t('auth.errors.tooManyRequests'),
            });
            break;
          case 'auth/user-disabled':
            setError('root', {
              type: 'manual',
              message: t('auth.errors.userDisabled'),
            });
            break;
          default:
            setError('root', {
              type: 'manual',
              message: firebaseError.message || t('auth.errors.loginFailed'),
            });
        }
      } else {
        setError('root', {
          type: 'manual',
          message: t('auth.errors.loginFailed'),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!phoneNumber) {
      setPhoneError(t('auth.errors.phoneRequired'));
      return;
    }
    setSendingSms(true);
    setPhoneError(null);

    try {
      const { auth } = await initializeFirebase();

      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }

      // grecaptcha tracks rendered containers by element reference internally.
      // Reusing the same element (even after innerHTML = '') is still seen as "already rendered".
      // Passing a brand-new child element every time guarantees grecaptcha has never seen it.
      const wrapper = document.getElementById('phone-login-recaptcha-wrapper');
      if (!wrapper) throw new Error('reCAPTCHA wrapper not found');
      wrapper.innerHTML = '';
      const freshContainer = document.createElement('div');
      wrapper.appendChild(freshContainer);

      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, freshContainer, {
        size: 'invisible',
      });

      const result = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifierRef.current);
      confirmationResultRef.current = result;
      setSmsSent(true);

      // Start 60-second resend countdown
      setCountdown(60);
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownRef.current!);
            countdownRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      if (err.code === 'auth/invalid-phone-number') {
        setPhoneError(t('auth.errors.invalidPhoneNumber'));
      } else if (err.code === 'auth/too-many-requests') {
        setPhoneError(t('auth.errors.tooManyRequests'));
      } else {
        setPhoneError(err.message || t('auth.errors.failedToSendCode'));
      }
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    } finally {
      setSendingSms(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!confirmationResultRef.current || !smsCode) return;
    setIsVerifying(true);
    setPhoneError(null);
    setWaitingForAuth(false);

    try {
      const userCred = await confirmationResultRef.current.confirm(smsCode);
      const idToken = await userCred.user.getIdToken();

      const res = await fetch('/api/shop/phone-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        setPhoneError(data.message || t('auth.errors.loginFailed'));
        return;
      }

      if (data.isNewUser) {
        resetPhoneState();
        try { sessionStorage.setItem('phoneLogin_verifiedPhone', data.phoneNumber); } catch {}
        navigate('/register');
        return;
      }

      if (data.customToken) {
        const { auth } = await initializeFirebase();
        await signInWithCustomToken(auth, data.customToken);
      }

      // AuthContext onAuthStateChanged listener calls getProfile() and sets isAuthenticated.
      // The existing waitingForAuth useEffect then navigates to `from`.
      setWaitingForAuth(true);
    } catch (err: any) {
      if (err.code === 'auth/invalid-verification-code') {
        setPhoneError(t('auth.errors.invalidCode'));
      } else if (err.code === 'auth/code-expired') {
        setPhoneError(t('auth.errors.codeExpired'));
      } else {
        setPhoneError(err.message || t('auth.errors.verificationFailed'));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // const handleGoogleSignIn = async () => {
  //   // Prevent multiple concurrent requests
  //   if (isGoogleLoading || isLoading) {
  //     return;
  //   }
  //
  //   try {
  //     setIsGoogleLoading(true);
  //     await loginWithGoogle();
  //     console.log('Google login successful, setting waiting state...');
  //     // Set waiting state - the useEffect will handle navigation when auth is ready
  //     setWaitingForAuth(true);
  //   } catch (error: unknown) {
  //     console.error('Google sign-in error:', error);
  //
  //     // Handle Google sign-in errors
  //     if (error && typeof error === 'object' && 'code' in error) {
  //       const firebaseError = error as { code: string; message?: string };
  //       switch (firebaseError.code) {
  //         case 'auth/popup-closed-by-user':
  //           setError('root', {
  //             type: 'manual',
  //             message: 'Sign-in was cancelled. Please try again.',
  //           });
  //           break;
  //         case 'auth/popup-blocked':
  //           setError('root', {
  //             type: 'manual',
  //             message: 'Pop-up was blocked. Please allow pop-ups and try again.',
  //           });
  //           break;
  //         case 'auth/cancelled-popup-request':
  //           setError('root', {
  //             type: 'manual',
  //             message: 'Please wait for the current sign-in to complete.',
  //           });
  //           break;
  //         default:
  //           setError('root', {
  //             type: 'manual',
  //             message: firebaseError.message || 'Google sign-in failed. Please try again.',
  //           });
  //       }
  //     } else {
  //       setError('root', {
  //         type: 'manual',
  //         message: 'Google sign-in failed. Please try again.',
  //       });
  //     }
  //   } finally {
  //     setIsGoogleLoading(false);
  //   }
  // };

  return (
    <Container
      maxWidth="xs"
      sx={{
        py: 8,
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
        <Typography variant="h5" fontWeight={700} align="center" gutterBottom>
          {t('auth.signIn')}
        </Typography>
        <Typography variant="body2" align="center" sx={{ mb: 0.5 }}>
          {t('brand.enjoyExclusiveServices')}
        </Typography>
        <Typography
          variant="subtitle2"
          align="center"
          sx={{ mb: 3, fontWeight: 700 }}
        >
          {t('brand.fromHatsuhiken')}
        </Typography>

        {errors.root && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.root.message}
          </Alert>
        )}

        {loginMode === 'email' && (
          <Stack spacing={2}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('auth.email')}
                  variant="outlined"
                  fullWidth
                  size="small"
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('auth.password')}
                  variant="outlined"
                  fullWidth
                  size="small"
                  type={showPassword ? 'text' : 'password'}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Controller
                name="saveId"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        size="small"
                        disabled={isLoading}
                      />
                    }
                    label={
                      <Typography variant="body2">{t('auth.saveId')}</Typography>
                    }
                    sx={{ mr: 2 }}
                  />
                )}
              />
              <Controller
                name="keepSignedIn"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        size="small"
                        disabled={isLoading}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {t('auth.keepSignedIn')}
                      </Typography>
                    }
                  />
                )}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isLoading || waitingForAuth}
              sx={{
                bgcolor: '#b8957b',
                color: 'white',
                fontWeight: 700,
                '&:hover': { bgcolor: '#a98c74' },
                '&:disabled': { bgcolor: '#ccc' },
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : waitingForAuth ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  {t('auth.signingIn')}
                </>
              ) : (
                t('auth.signIn')
              )}
            </Button>

            <Box>
              <Link
                component="button"
                onClick={() => navigate('/forgot-password')}
                underline="none"
                sx={{ fontSize: 14, color: 'text.secondary', cursor: 'pointer' }}
              >
                {t('auth.forgotPassword')}
              </Link>
            </Box>

            <Divider>{t('auth.or')}</Divider>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<Phone />}
              onClick={() => {
                resetPhoneState();
                setLoginMode('phone');
              }}
              sx={{
                borderColor: '#b8957b',
                color: '#b8957b',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { borderColor: '#a98c74', backgroundColor: 'rgba(184, 149, 123, 0.04)' },
              }}
            >
              {t('auth.loginWithPhone')}
            </Button>
          </Stack>
        )}

        {loginMode === 'phone' && (
          <Stack spacing={2}>
            <MuiTelInput
              value={phoneNumber}
              onChange={(val) => {
                setPhoneNumber(val);
                if (smsSent) {
                  setSmsSent(false);
                  setSmsCode('');
                  confirmationResultRef.current = null;
                  setPhoneError(null);
                  if (countdownRef.current) {
                    clearInterval(countdownRef.current);
                    countdownRef.current = null;
                  }
                  setCountdown(0);
                }
              }}
              defaultCountry="HK"
              forceCallingCode={true}
              fullWidth
              size="small"
              variant="outlined"
              placeholder={t('auth.phoneNumberPlaceholder')}
              disabled={sendingSms || isVerifying}
            />

            <Button
              variant="contained"
              fullWidth
              onClick={handleSendCode}
              disabled={sendingSms || countdown > 0 || !phoneNumber}
              sx={{
                bgcolor: '#b8957b',
                color: 'white',
                fontWeight: 700,
                '&:hover': { bgcolor: '#a98c74' },
                '&:disabled': { bgcolor: '#ccc', color: '#999' },
              }}
            >
              {sendingSms ? (
                <CircularProgress size={20} color="inherit" />
              ) : countdown > 0 ? (
                `${t('auth.resendCode')} (${countdown}s)`
              ) : smsSent ? (
                t('auth.resendCode')
              ) : (
                t('auth.sendCode')
              )}
            </Button>

            {/*
              Invisible reCAPTCHA anchor.
              IMPORTANT: Never conditionally render this div while a verifier may be active.
              Removing it from the DOM detaches the reCAPTCHA widget and causes errors on resend.
            */}
            <div id="phone-login-recaptcha-wrapper" />

            {smsSent && (
              <>
                <TextField
                  label={t('auth.verificationCode')}
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder={t('auth.verificationCodePlaceholder')}
                  inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                  disabled={isVerifying}
                />

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleVerifyCode}
                  disabled={isVerifying || smsCode.length !== 6 || waitingForAuth}
                  sx={{
                    bgcolor: '#b8957b',
                    color: 'white',
                    fontWeight: 700,
                    '&:hover': { bgcolor: '#a98c74' },
                    '&:disabled': { bgcolor: '#ccc', color: '#999' },
                  }}
                >
                  {isVerifying || waitingForAuth ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      {t('auth.signingIn')}
                    </>
                  ) : (
                    t('auth.verify')
                  )}
                </Button>
              </>
            )}

            {phoneError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {phoneError}
              </Alert>
            )}

            <Divider>{t('auth.or')}</Divider>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<Email />}
              onClick={() => {
                resetPhoneState();
                setLoginMode('email');
              }}
              sx={{
                borderColor: '#b8957b',
                color: '#b8957b',
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { borderColor: '#a98c74', backgroundColor: 'rgba(184, 149, 123, 0.04)' },
              }}
            >
              {t('auth.loginWithEmail')}
            </Button>
          </Stack>
        )}

        <Divider sx={{ my: 3 }}>{t('auth.or')}</Divider>

        <Stack spacing={1}>
          {/* <Button
            variant="outlined"
            fullWidth
            startIcon={
              <img src="/icons/line.svg" alt="LINE" width={24} height={24} />
            }
            sx={{
              borderColor: '#b8957b',
              color: '#b8957b',
              textTransform: 'none',
              fontWeight: 500,
            }}
            disabled={isLoading}
          >
            {t('auth.signInWith')} {t('social.line')}
          </Button> */}
          {/* <Button
            variant="outlined"
            fullWidth
            startIcon={
              isGoogleLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <img
                  src="/icons/google.svg"
                  alt="Google"
                  width={24}
                  height={24}
                />
              )
            }
            onClick={handleGoogleSignIn}
            sx={{
              borderColor: '#b8957b',
              color: '#b8957b',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#a98c74',
                backgroundColor: 'rgba(184, 149, 123, 0.04)',
              },
              '&:disabled': {
                borderColor: '#ccc',
                color: '#ccc'
              },
            }}
            disabled={isLoading || isGoogleLoading || waitingForAuth}
          >
            {isGoogleLoading
              ? 'Signing in...'
              : waitingForAuth
              ? 'Preparing...'
              : `${t('auth.signInWith')} ${t('social.google')}`
            }
          </Button> */}
          {/* <Button
            variant="outlined"
            fullWidth
            startIcon={
              <img src="/icons/apple.svg" alt="Apple" width={24} height={24} />
            }
            sx={{
              borderColor: '#b8957b',
              color: '#b8957b',
              textTransform: 'none',
              fontWeight: 500,
            }}
            disabled={isLoading}
          >
            {t('auth.signInWith')} {t('social.apple')}
          </Button> */}
        </Stack>

        <Box sx={{ mt: 5, mb: 2, textAlign: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            {t('benefits.dontMissOut')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('benefits.hatsuhikenBenefits')}
          </Typography>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 2 }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <img
                src="/icons/ic_b_day_off.svg"
                alt="B-DAY"
                width={40}
                height={40}
              />
              <Typography
                variant="caption"
                sx={{ color: '#b8957b', fontWeight: 500, mt: 1 }}
              >
                {t('benefits.bdayOff')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <img
                src="/icons/ic_app_point.svg"
                alt="Points"
                width={40}
                height={40}
              />
              <Typography
                variant="caption"
                sx={{ color: '#b8957b', fontWeight: 500, mt: 1 }}
              >
                {t('benefits.appInstallPoints')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 4, textAlign: 'center', width: '100%' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('auth.dontHaveAccount')}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate('/register')}
          sx={{
            bgcolor: '#b8957b',
            color: 'white',
            fontWeight: 700,
            '&:hover': { bgcolor: '#a98c74' },
            '&:disabled': { bgcolor: '#ccc' },
          }}
        >
          {t('auth.joinNow')}
        </Button>
      </Box>
    </Container>
  );
}
